import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Table, Tag, Modal, Form, Input, Select } from "antd";
import { FiUsers, FiPlus, FiEdit, FiTrash2, FiMail, FiShield, FiUserCheck } from "react-icons/fi";

export default function UserManagementPage() {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editingUser, setEditingUser] = useState(null);
	const [form] = Form.useForm();

	// Users Data
	const usersData = [
		{
			id: "USR-001",
			name: "Raj Sharma",
			email: "raj@sec1.io",
			role: "Admin",
			department: "Security",
			status: "Active",
			joinDate: "2024-06-15",
			lastLogin: "2025-01-02 14:30:00",
			permissions: ["Read", "Write", "Delete", "Admin"],
		},
		{
			id: "USR-002",
			name: "Dinesh Rawat",
			email: "dinesh.rawat@sec1.io",
			role: "Member",
			department: "Operations",
			status: "Active",
			joinDate: "2024-08-20",
			lastLogin: "2025-01-02 13:45:00",
			permissions: ["Read", "Write"],
		},
		{
			id: "USR-003",
			name: "Saurabh Thatte",
			email: "saurabh.thatte@sec1.io",
			role: "Member",
			department: "Network Team",
			status: "Active",
			joinDate: "2024-09-10",
			lastLogin: "2025-01-02 12:15:00",
			permissions: ["Read", "Write"],
		},
		{
			id: "USR-004",
			name: "Rahul Darekar",
			email: "rahul.darekar@sec1.io",
			role: "Member",
			department: "Security",
			status: "Active",
			joinDate: "2024-07-05",
			lastLogin: "2025-01-01 16:20:00",
			permissions: ["Read", "Write"],
		},
		{
			id: "USR-005",
			name: "Prashant Kanawde",
			email: "prashant.kanawde@sec1.io",
			role: "Member",
			department: "Maintenance",
			status: "Active",
			joinDate: "2024-10-18",
			lastLogin: "2024-12-31 10:00:00",
			permissions: ["Read"],
		},
		{
			id: "USR-006",
			name: "Vinayak Padwalkar",
			email: "vinayak.padwalkar@sec1.io",
			role: "Member",
			department: "Infrastructure",
			status: "Active",
			joinDate: "2024-11-22",
			lastLogin: "2025-01-02 11:30:00",
			permissions: ["Read", "Write"],
		},
	];

	// Summary Statistics
	const userStats = {
		totalUsers: 6,
		activeUsers: 6,
		admins: 1,
		members: 5,
	};

	// Helper functions
	const getRoleColor = (role) => {
		return role === "Admin" ? "#ef4444" : "#3b82f6";
	};

	const getStatusColor = (status) => {
		return status === "Active" ? "#10b981" : "#6b7280";
	};

	const getDepartmentColor = (dept) => {
		const colors = {
			Security: "#ef4444",
			Operations: "#f59e0b",
			"Network Team": "#3b82f6",
			Maintenance: "#8b5cf6",
			Infrastructure: "#10b981",
		};
		return colors[dept] || "#6b7280";
	};

	const handleAddUser = () => {
		setEditingUser(null);
		form.resetFields();
		setIsModalVisible(true);
	};

	const handleEditUser = (user) => {
		setEditingUser(user);
		form.setFieldsValue(user);
		setIsModalVisible(true);
	};

	const handleModalOk = () => {
		form.validateFields().then(() => {
			setIsModalVisible(false);
		});
	};

	const handleModalCancel = () => {
		setIsModalVisible(false);
	};

	// Table columns
	const userColumns = [
		{
			title: "User ID",
			dataIndex: "id",
			key: "id",
			render: (text) => <span className="fw-bold text-gray-900">{text}</span>,
			width: 100,
		},
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (text) => <span className="fw-semibold text-gray-900">{text}</span>,
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			render: (email) => (
				<div className="d-flex align-items-center gap-2">
					<FiMail size={14} />
					<span className="text-gray-700">{email}</span>
				</div>
			),
		},
		{
			title: "Role",
			dataIndex: "role",
			key: "role",
			render: (role) => (
				<Tag color={getRoleColor(role)} style={{ borderRadius: "4px", fontWeight: "600" }}>
					{role === "Admin" ? (
						<span className="d-flex align-items-center gap-1">
							<FiShield size={12} />
							{role}
						</span>
					) : (
						role
					)}
				</Tag>
			),
			width: 100,
		},
		{
			title: "Department",
			dataIndex: "department",
			key: "department",
			render: (dept) => <Tag style={{ borderRadius: "4px", background: `rgba(${getRgb(getDepartmentColor(dept))}, 0.1)`, color: getDepartmentColor(dept) }}>{dept}</Tag>,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color={getStatusColor(status)} style={{ borderRadius: "4px", fontWeight: "600" }}>
					{status}
				</Tag>
			),
			width: 100,
		},
		{
			title: "Join Date",
			dataIndex: "joinDate",
			key: "joinDate",
			width: 120,
		},
		{
			title: "Last Login",
			dataIndex: "lastLogin",
			key: "lastLogin",
			width: 160,
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<div className="d-flex gap-2">
					<button
						type="button"
						className="btn btn-sm btn-light-primary fw-bold"
						onClick={() => handleEditUser(record)}
						title="Edit user"
					>
						<FiEdit size={14} />
					</button>
					<button type="button" className="btn btn-sm btn-light-danger fw-bold" title="Delete user">
						<FiTrash2 size={14} />
					</button>
				</div>
			),
			width: 100,
		},
	];

	// Utility function to convert color to RGB
	const getRgb = (color) => {
		const hexToRgb = (hex) => {
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : "0, 0, 0";
		};
		return hexToRgb(color);
	};

	return (
		<>
			<Helmet>
				<title>User Management | Sec1 Software Pvt. Ltd.</title>
			</Helmet>

			<div id="kt_app_toolbar" className="app-toolbar pt-12 rounded-0">
				<div id="kt_app_toolbar_container" className="app-container container-fluid d-flex align-items-stretch">
					<div className="app-toolbar-wrapper d-flex flex-stack flex-wrap gap-4 w-100">
						<div className="page-title d-flex flex-column gap-1 me-3 mb-2">
							<h1 className="page-heading d-flex flex-column justify-content-center text-dark fw-bolder fs-1 lh-0">
								User Management
							</h1>
						</div>
					</div>
				</div>
			</div>

			<div id="kt_app_content" className="app-content flex-column-fluid">
				<div id="kt_app_content_container" className="app-container container-fluid">
					{/* Statistics Cards */}
					<div className="mb-3">
						<div className="row g-2">
							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiUsers className="fs-2hx text-primary" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{userStats.totalUsers}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Total Users</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiUserCheck className="fs-2hx text-success" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{userStats.activeUsers}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Active Users</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiShield className="fs-2hx text-warning" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{userStats.admins}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Administrators</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col">
								<div className="card h-lg-100">
									<div className="card-body d-flex justify-content-between align-items-start flex-column py-4 px-6">
										<div className="m-0">
											<FiUsers className="fs-2hx text-info" style={{ fontSize: "28px" }} />
										</div>
										<div className="d-flex flex-column my-3 mb-0">
											<span className="fw-semibold fs-3x text-gray-800 lh-1 ls-n2">{userStats.members}</span>
											<div className="m-0">
												<span className="fw-semibold fs-6 text-gray-500">Team Members</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Users Table Section */}
					<div className="card">
						<div className="card-header border-0 pt-5">
							<h3 className="card-title align-items-start flex-column">
								<span className="card-label fw-bold text-dark">Team Members</span>
								<span className="text-muted mt-1 fw-semibold fs-7">All users and their role assignments</span>
							</h3>
							<div className="card-toolbar">
								<button
									type="button"
									className="btn btn-sm btn-primary fw-bold d-flex align-items-center gap-2"
									onClick={handleAddUser}
								>
									<FiPlus size={16} />
									Add New User
								</button>
							</div>
						</div>
						<div className="card-body">
							<Table
								dataSource={usersData}
								columns={userColumns}
								pagination={{ pageSize: 10, total: usersData.length }}
								scroll={{ x: 1400 }}
								className="antd-override"
								rowKey={(record) => record.id}
								rowClassName={(record) => record.role === "Admin" ? "table-row-highlighted" : ""}
							/>
						</div>
					</div>

					{/* User Roles & Permissions Info Card */}
					<div className="card mt-3">
						<div className="card-header border-0 pt-5">
							<h3 className="card-title align-items-start flex-column">
								<span className="card-label fw-bold text-dark">Role & Permissions Guide</span>
								<span className="text-muted mt-1 fw-semibold fs-7">Overview of available roles and their permissions</span>
							</h3>
						</div>
						<div className="card-body">
							<div className="row g-3">
								{/* Admin Role */}
								<div className="col-12 col-lg-6">
									<div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #ef4444", boxShadow: "0 2px 8px rgba(239, 68, 68, 0.08)" }}>
										<div className="d-flex align-items-center gap-2 mb-3">
											<FiShield size={20} color="#ef4444" />
											<h6 className="fw-bold text-gray-900 mb-0">Administrator</h6>
											<Tag color="#ef4444" style={{ borderRadius: "4px", fontWeight: "600", fontSize: "11px" }}>HIGHEST</Tag>
										</div>
										<p className="text-gray-600 fs-6 mb-3">Full system access with all permissions</p>
										<ul className="ps-3 mb-0">
											<li className="text-gray-700 fs-6 mb-1">Full Read & Write Access</li>
											<li className="text-gray-700 fs-6 mb-1">User & Role Management</li>
											<li className="text-gray-700 fs-6 mb-1">System Configuration</li>
											<li className="text-gray-700 fs-6">Audit Log Access</li>
										</ul>
									</div>
								</div>

								{/* Member Role */}
								<div className="col-12 col-lg-6">
									<div className="p-4 border rounded" style={{ borderColor: "#e5e7eb", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(0, 0, 0, 0) 100%)", borderLeft: "4px solid #3b82f6", boxShadow: "0 2px 8px rgba(59, 130, 246, 0.08)" }}>
										<div className="d-flex align-items-center gap-2 mb-3">
											<FiUsers size={20} color="#3b82f6" />
											<h6 className="fw-bold text-gray-900 mb-0">Team Member</h6>
											<Tag color="#3b82f6" style={{ borderRadius: "4px", fontWeight: "600", fontSize: "11px" }}>STANDARD</Tag>
										</div>
										<p className="text-gray-600 fs-6 mb-3">Limited access based on department role</p>
										<ul className="ps-3 mb-0">
											<li className="text-gray-700 fs-6 mb-1">Read & Write Access</li>
											<li className="text-gray-700 fs-6 mb-1">Department Data Access</li>
											<li className="text-gray-700 fs-6 mb-1">Report Generation</li>
											<li className="text-gray-700 fs-6">No User Management</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Add/Edit User Modal */}
			<Modal
				title={editingUser ? "Edit User" : "Add New User"}
				visible={isModalVisible}
				onOk={handleModalOk}
				onCancel={handleModalCancel}
				width={600}
			>
				<Form form={form} layout="vertical">
					<Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Please enter name" }]}>
						<Input placeholder="Enter full name" />
					</Form.Item>
					<Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "Please enter valid email" }]}>
						<Input placeholder="Enter email address" />
					</Form.Item>
					<Form.Item label="Department" name="department" rules={[{ required: true, message: "Please select department" }]}>
						<Select placeholder="Select department">
							<Select.Option value="Security">Security</Select.Option>
							<Select.Option value="Operations">Operations</Select.Option>
							<Select.Option value="Network Team">Network Team</Select.Option>
							<Select.Option value="Maintenance">Maintenance</Select.Option>
							<Select.Option value="Infrastructure">Infrastructure</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item label="Role" name="role" rules={[{ required: true, message: "Please select role" }]}>
						<Select placeholder="Select role">
							<Select.Option value="Admin">Admin</Select.Option>
							<Select.Option value="Member">Member</Select.Option>
						</Select>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
}
